import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import _get from 'lodash-es/get';
import _noop from 'lodash-es/noop';
import H4 from 'sushiweb/Typography/Heading/H4';
import Layout from 'sushiweb/Layout/Layout';
import Button from 'sushiweb/Button/Button';
import Spinner from 'sushiweb/Spinner/Spinner';
import red from 'sushiweb/tokens/color/red';
import { SingleLineArrow } from '../../../components/SVGs/commonSVGs';
import {
  GET_PAYMENT_CODE,
  GET_PAYMENT_REQUEST_LOG,
} from '../../../constants/urls';
import zfetch from '../../common/utils/zfetch';
import {
  HeadWrapper,
  LayoutContainers,
  DashboardWrapper,
  OnlinePaymentHeader,
  SpinnerHolder,
} from '../../common/styles';

import {
  formatAmountInRupees,
  getPaymentRequestLogStatusMessage,
  getServiceTypeFromFlowType,
} from '../../../helpers';
import ErrorPage from '../../common/components/ErrorPage/ErrorPage';
import PaymentKit from '../OnlinePaymentCheckoutPage/PaymentKit';
import { paymentRequestEnum } from '../../common/components/constants';
import { CheckoutBtnContainer } from '../styles';

const AdhocPaymentsPage = ({
  paymentsInfo,
  getOnlinePaymentInfo,
  fetchingOnlinePaymentInfo,
  fetchedOnlinePaymentInfo,
}) => {
  const { paymentRequestID } = useParams();
  const [paymentSDKData, setPaymentSDKData] = useState({});
  const [accessCode, setAccessCode] = useState('');
  const [amountBalance, setAmountBalance] = useState(0);
  const [isAccessCodeFetched, setIsAccessCodeFetched] = useState(true);
  const [isPaymentRequestFetched, setIsPaymentRequestFetched] = useState(false);
  const [paymentRequestStatus, setPaymentRequestStatus] = useState(
    paymentRequestEnum.UNSPECIFIED,
  );
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [paymentKitVisibility, setPaymentKitVisibility] = useState(false);
  const [isCheckoutStarted, setIsCheckoutStarted] = useState(false);
  const [isCheckoutEnded, setIsCheckoutEnded] = useState(false);
  const [fetchingAccessCode, setFetchingAccessCode] = useState(true);
  const [fetchingPaymentRequest, setFetchingPaymentRequest] = useState(true);
  const [currentFlowType, setCurrentFlowType] = useState(0);
  const checkoutPayment = () => {
    setIsCheckoutStarted(true);
    setLoadingCheckout(true);
  };

  useEffect(() => {
    if (isPaymentRequestFetched)
      getOnlinePaymentInfo({
        paymentRequestID,
        flowType: getServiceTypeFromFlowType(currentFlowType),
      });
  }, [isPaymentRequestFetched]);

  useEffect(() => {
    if (
      !fetchingOnlinePaymentInfo &&
      fetchedOnlinePaymentInfo &&
      paymentsInfo &&
      isPaymentRequestFetched
    ) {
      setPaymentSDKData({
        amount: amountBalance,
        country_id: paymentsInfo.country_id ?? 1,
        service_type: paymentsInfo.service_type ?? '',
        company_id: paymentsInfo.company_id ?? '1',
        online_payments_flag: paymentsInfo.online_payments_flag ?? 1,
        user_details: {
          user_email: paymentsInfo?.user_details?.email ?? '',
          phone: paymentsInfo?.user_details?.phone ?? '',
        },
      });
    }
  }, [fetchingOnlinePaymentInfo, fetchingPaymentRequest]);

  useEffect(() => {
    if (isPaymentRequestFetched) {
      zfetch(GET_PAYMENT_CODE, {
        params: {
          service_type: getServiceTypeFromFlowType(currentFlowType),
          payment_request_id: paymentRequestID,
        },
      })
        .then(res => res.json())
        .then(data => {
          setAccessCode(_get(data, 'token'));
          setIsAccessCodeFetched(true);
          setFetchingAccessCode(false);
        })
        .catch(error => {
          setIsAccessCodeFetched(false);
          setFetchingAccessCode(false);
          console.error(error);
        });
    }
  }, [isPaymentRequestFetched]);

  useEffect(() => {
    zfetch(GET_PAYMENT_REQUEST_LOG, {
      params: {
        payment_request_id: paymentRequestID,
      },
    })
      .then(res => res.json())
      .then(data => {
        setAmountBalance(_get(data, 'data.amount', 0));
        setPaymentRequestStatus(_get(data, 'data.status', 0));
        setCurrentFlowType(_get(data, 'data.flow_type', 0));
        setIsPaymentRequestFetched(_get(data, 'status_response.status', false));
        setFetchingPaymentRequest(false);
      })
      .catch(error => {
        setIsPaymentRequestFetched(false);
        setFetchingPaymentRequest(false);
        console.error(error);
      });
  }, []);

  if (
    fetchingOnlinePaymentInfo ||
    fetchingAccessCode ||
    fetchingPaymentRequest
  ) {
    return (
      <DashboardWrapper>
        <SpinnerHolder className='center'>
          <Spinner color={red.z_red} size='medium' />
        </SpinnerHolder>
      </DashboardWrapper>
    );
  }
  if (
    paymentRequestStatus !== paymentRequestEnum.ACTIVE &&
    paymentRequestStatus !== paymentRequestEnum.UNSPECIFIED
  ) {
    return (
      <>
        <HeadWrapper>
          <OnlinePaymentHeader>
            <H4>
              {getPaymentRequestLogStatusMessage(
                paymentRequestStatus,
                currentFlowType,
              )}
            </H4>
          </OnlinePaymentHeader>
        </HeadWrapper>
      </>
    );
  }

  return (
    <>
      <DashboardWrapper>
        {fetchedOnlinePaymentInfo &&
          isAccessCodeFetched &&
          isPaymentRequestFetched && (
            <HeadWrapper>
              <OnlinePaymentHeader>
                <H4>
                  Amount to be paid: &nbsp;
                  <div id='amount'>
                    {formatAmountInRupees(Number.parseFloat(amountBalance))}
                  </div>
                </H4>
              </OnlinePaymentHeader>
            </HeadWrapper>
          )}
        <LayoutContainers style={{ padding: '30px 0px 0px 0px' }}>
          <Layout gap={20}>
            <Layout.Item colD={12} colT={12} colM={12}>
              {fetchedOnlinePaymentInfo &&
                isAccessCodeFetched &&
                isPaymentRequestFetched ? (
                  <PaymentKit
                    paymentKitVisibility={paymentKitVisibility}
                    setPaymentKitVisibility={setPaymentKitVisibility}
                    paymentSdkData={paymentSDKData}
                    accessCode={accessCode}
                    isCheckoutStarted={isCheckoutStarted}
                    isCheckoutEnded={isCheckoutEnded}
                    setIsCheckoutEnded={setIsCheckoutEnded}
                    initiateCheckout={checkoutPayment}
                    setIsCheckoutStarted={setIsCheckoutStarted}
                    setLoadingCheckout={setLoadingCheckout}
                    loggedOutInfo={{
                    balance: amountBalance,
                    payment_request_id: paymentRequestID,
                  }}
                    adhocState
                  />
              ) : (
                <ErrorPage />
              )}
            </Layout.Item>
            <CheckoutBtnContainer>
              <Button
                onClick={checkoutPayment}
                loading={loadingCheckout}
                style={{ paddingRight: '30px' }}
                icon={<SingleLineArrow />}
                iconAlign='right'
                disabled={
                  !fetchedOnlinePaymentInfo ||
                  !isAccessCodeFetched ||
                  !isPaymentRequestFetched
                }
              >
                {' '}
                Pay Now
                {' '}
              </Button>
            </CheckoutBtnContainer>
          </Layout>
        </LayoutContainers>
      </DashboardWrapper>
    </>
  );
};

AdhocPaymentsPage.propTypes = {
  getOnlinePaymentInfo: PropTypes.func,
  paymentsInfo: PropTypes.objectOf(PropTypes.any),
  fetchingOnlinePaymentInfo: PropTypes.bool,
  fetchedOnlinePaymentInfo: PropTypes.bool,
};

AdhocPaymentsPage.defaultProps = {
  getOnlinePaymentInfo: _noop,
  paymentsInfo: {},
  fetchingOnlinePaymentInfo: true,
  fetchedOnlinePaymentInfo: true,
};

export default AdhocPaymentsPage;
